import { useMemo, useState } from "react";
import { toast } from "sonner";
import { FileDown } from "lucide-react";
import { Section, Empty, FilterBar } from "./CommonUI";
import { INR_P, INVOICE_TONE, fmtDate } from "./clientUtils";
import { clientNonProjectFileUrl } from "@/lib/api";

function DownloadButton({ row, token, financeToken }) {
  const download = async () => {
    try {
      const res = await fetch(clientNonProjectFileUrl(row.id), {
        headers: { Authorization: `Bearer ${token}`, "X-Finance-Token": financeToken },
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = row.attachment?.filename || `${row.invoice_number || "invoice"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch {
      toast.error("Failed to download invoice file");
    }
  };
  return (
    <button onClick={download} data-testid={`additional-invoice-download-${row.id}`}
      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-[#ff5722]/30 text-[#ff5722] rounded-full hover:bg-[#ff5722] hover:text-white transition-colors">
      <FileDown className="h-3.5 w-3.5" /> PDF
    </button>
  );
}

const HEADERS = ["#", "Invoice", "Additional work", "Issued", "Due", "Total", "Paid", "Balance", "Status", "File"];
const RIGHT = new Set(["Total", "Paid", "Balance", "File"]);

export default function AdditionalPaymentsTable({ rows, loading, onRefresh, token, financeToken }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const filtered = useMemo(() => rows.filter((r) => {
    const matchesQ = !q || `${r.invoice_number} ${r.work_title}`.toLowerCase().includes(q.toLowerCase());
    return matchesQ && (status === "all" || r.status === status);
  }), [rows, q, status]);

  return (
    <Section title="Additional Payments" subtitle="Invoices for additional work delivered outside your projects." onRefresh={onRefresh} loading={loading}
      toolbar={<FilterBar q={q} onQ={setQ} value={status} onValue={setStatus} options={["all", "draft", "sent", "partially_paid", "paid", "overdue"]} placeholder="Search invoice #, work…" />}>
      {filtered.length === 0 ? (
        <Empty msg={rows.length === 0 ? "No additional work invoices yet." : "No invoices match your filter."} />
      ) : (
        <div className="overflow-x-auto" data-testid="additional-payments-table">
          <table className="w-full text-left min-w-[900px]">
            <thead className="bg-[#f7f6f3] border-b border-black/10 text-[11px] font-mono uppercase tracking-[0.2em] text-[#4a4a4a]">
              <tr>{HEADERS.map((h) => <th key={h} className={`px-4 py-3 ${RIGHT.has(h) ? "text-right" : ""}`}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr key={r.id} className="border-b border-black/5 hover:bg-[#0a0a0a]/[0.02]" data-testid={`additional-invoice-row-${r.id}`}>
                  <td className="px-4 py-4 text-xs font-mono text-[#4a4a4a]">{String(i + 1).padStart(2, "0")}</td>
                  <td className="px-4 py-4 font-mono font-bold text-[#0a0a0a]">{r.invoice_number || "—"}</td>
                  <td className="px-4 py-4">
                    <div className="text-sm font-semibold">{r.work_title}</div>
                    {r.description && <div className="text-xs text-[#4a4a4a] mt-0.5 line-clamp-2">{r.description}</div>}
                  </td>
                  <td className="px-4 py-4 text-xs font-mono">{fmtDate(r.issued_date)}</td>
                  <td className="px-4 py-4 text-xs font-mono">{fmtDate(r.due_date)}</td>
                  <td className="px-4 py-4 text-right font-mono font-bold">{INR_P(r.total ?? r.amount)}</td>
                  <td className="px-4 py-4 text-right font-mono text-emerald-700">{INR_P(r.paid_amount)}</td>
                  <td className={`px-4 py-4 text-right font-mono ${r.balance > 0 ? "text-rose-700 font-bold" : "text-[#4a4a4a]"}`}>{INR_P(r.balance)}</td>
                  <td className="px-4 py-4"><span className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-[0.1em] rounded whitespace-nowrap ${INVOICE_TONE[r.status] || "bg-zinc-100 text-zinc-700"}`}>{(r.status || "").replace("_", " ")}</span></td>
                  <td className="px-4 py-4 text-right">
                    {r.has_attachment ? <DownloadButton row={r} token={token} financeToken={financeToken} /> : <span className="text-xs text-[#9a9a9a]">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}
